import { useState, useRef, useEffect } from 'react';
import './index.css';
import { I } from './data/tokens';
import { NAV_ITEMS, CASE_STUDIES } from './data';
import Sidebar         from './components/Sidebar';
import MobileNav       from './components/MobileNav';
import CaseStudyDetail from './components/CaseStudyDetail';
import {
  HeroSection,
  AboutSection,
  ProjectsSection,
  CareerSection,
  FooterSection,
} from './components/HeroSection';

// Original CSS injected as a style tag (exact original behaviour)
const GLOBAL_CSS = `
  * { margin: 0; padding: 0; box-sizing: border-box; }
  ::-webkit-scrollbar { width: 4px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.15); border-radius: 2px; }
  .fade-up { animation: fadeUp 0.9s cubic-bezier(0.22, 1, 0.36, 1) both; }
  @keyframes fadeUp { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: translateY(0); } }
  .scroll-line { animation: scrollPulse 2s ease-in-out infinite; }
  @keyframes scrollPulse { 0%, 100% { opacity: 0.2; } 50% { opacity: 0.6; } }
  .detail-enter { animation: detailSlide 0.38s cubic-bezier(0.22, 1, 0.36, 1) both; }
  @keyframes detailSlide { from { opacity: 0; transform: translateY(18px); } to { opacity: 1; transform: translateY(0); } }
  button:hover { opacity: 0.75; }
  a[href]:hover { opacity: 0.7; }
  li { list-style: none; }

  /* Mobile Nav */
  .mobile-header { display: none; }
  .mobile-nav-overlay {
    position: fixed; inset: 0; background: #F7F5F2; z-index: 300;
    display: flex; flex-direction: column; padding: 1.5rem;
    animation: fadeUp 0.25s ease both;
  }
  .mobile-nav-overlay-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 1.5rem; }
  .mobile-nav-btn {
    background: none; border: none; cursor: pointer; width: 100%;
    font-family: 'DM Serif Display', Georgia, serif; font-size: 1.8rem;
    color: #1A1A1A; text-align: left; padding: 1rem 0;
    border-bottom: 1px solid rgba(0,0,0,0.06);
    display: flex; align-items: center; gap: 1rem;
  }
  .mobile-nav-btn .num { font-family: 'DM Mono', monospace; font-size: 0.65rem; color: #999; letter-spacing: 0.06em; }
  .hamburger { background: none; border: none; cursor: pointer; padding: 0.3rem; display: flex; flex-direction: column; gap: 5px; z-index: 301; }
  .hamburger span { display: block; width: 22px; height: 1.5px; background: #1A1A1A; transition: all 0.28s ease; }
  .hamburger.open span:nth-child(1) { transform: translateY(6.5px) rotate(45deg); }
  .hamburger.open span:nth-child(2) { opacity: 0; }
  .hamburger.open span:nth-child(3) { transform: translateY(-6.5px) rotate(-45deg); }
  .mobile-brand { display: flex; align-items: center; gap: 0.5rem; }
  .mobile-brand-dot { width: 7px; height: 7px; border-radius: 50%; background: #1A1A1A; }
  .mobile-brand-name { font-family: 'DM Mono', monospace; font-size: 0.78rem; letter-spacing: 0.03em; color: #1A1A1A; }

  /* Tablet */
  @media (max-width: 1024px) {
    .aside-desktop { width: 180px !important; min-width: 180px !important; padding: 2rem 1.5rem !important; }
    .section-pad { padding: 4rem 3.5rem 3rem !important; }
  }
  /* Mobile */
  @media (max-width: 768px) {
    .aside-desktop { display: none !important; }
    .mobile-header { display: flex !important; align-items: center; justify-content: space-between; padding: 1rem 1.5rem; border-bottom: 1px solid rgba(0,0,0,0.07); background: #F7F5F2; position: sticky; top: 0; z-index: 50; flex-shrink: 0; width: 100%; }
    .section-pad { padding: 2.5rem 1.5rem 2rem !important; min-height: unset !important; }
    .footer-section { padding: 2.5rem 1.5rem 2rem !important; }
    .hero-scroll { display: none !important; }
    .cs-metrics { grid-template-columns: repeat(2,1fr) !important; }
    .cs-strategy-grid { grid-template-columns: 1fr !important; }
    .cs-journey-grid { grid-template-columns: 1fr !important; }
    .cs-impact-stats { grid-template-columns: repeat(2,1fr) !important; }
    .cs-quotes-grid { grid-template-columns: 1fr !important; }
    .cs-learnings-grid { grid-template-columns: 1fr !important; }
    .detail-top-bar { padding: 0.85rem 1.25rem !important; }
    .detail-stats-strip { flex-direction: column !important; }
    .detail-stat-cell { border-right: none !important; border-bottom: 1px solid rgba(0,0,0,0.07) !important; }
    .cs-hero { padding: 2rem 1.5rem !important; }
    .cs-section { padding: 1.75rem 1.5rem !important; }
    .cs-gallery { padding: 1.5rem !important; }
    .cs-gallery > div:last-child { grid-template-columns: repeat(2,1fr) !important; }
    .nav-btn-title { display: none; }
    .footer-bar { flex-direction: column !important; gap: 0.4rem !important; }
  }

  /* Case Study shared styles */
  .cs-hero { padding: 3.5rem 3rem 3rem; color: white; position: relative; overflow: hidden; }
  .cs-section { padding: 2.5rem 3rem; border-bottom: 1px solid rgba(0,0,0,0.06); background: #fff; }
  .cs-gallery { padding: 2rem 3rem; border-bottom: 1px solid rgba(0,0,0,0.06); background: #fff; }
  .detail-stats-strip { display: flex; border-bottom: 1px solid rgba(0,0,0,0.07); }
  .detail-stat-cell { flex: 1; padding: 1.25rem 3rem; display: flex; flex-direction: column; gap: 0.3rem; border-right: 1px solid rgba(0,0,0,0.07); }
  .detail-stat-cell:last-child { border-right: none; }
  .detail-top-bar { display: flex; align-items: center; justify-content: space-between; padding: 1.1rem 3rem; border-bottom: 1px solid rgba(0,0,0,0.07); flex-shrink: 0; background: #F7F5F2; }
  .detail-nav-pad { display: flex; align-items: center; justify-content: space-between; padding: 0.9rem 3rem; border-top: 1px solid rgba(0,0,0,0.07); flex-shrink: 0; background: #F7F5F2; }
  .detail-wrapper { position: absolute; inset: 0; background: #F7F5F2; display: flex; flex-direction: column; z-index: 20; }
`;

export default function App() {
  const [activeSection, setActiveSection] = useState('hero');
  const [hoveredNav,    setHoveredNav]    = useState(null);
  const [openProject,   setOpenProject]  = useState(null);
  const [mobileOpen,    setMobileOpen]   = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  const scrollRef   = useRef(null);
  const sectionRefs = useRef({});

  const setRef = (id) => (el) => { sectionRefs.current[id] = el; };

  const scrollTo = (id) => {
    const el = sectionRefs.current[id];
    if (!el) return;
    if (id === 'footer') {
      scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
    } else {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Active section observer — exact original logic
  useEffect(() => {
    const scroller = scrollRef.current;
    if (!scroller) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) setActiveSection(e.target.id); }),
      { root: scroller, threshold: 0, rootMargin: '-10% 0px -80% 0px' }
    );
    Object.values(sectionRefs.current).forEach((el) => { if (el) obs.observe(el); });

    // Mark footer active when scrolled to bottom
    const onScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scroller;
      if (scrollTop + clientHeight >= scrollHeight - 10) setActiveSection('footer');
    };
    scroller.addEventListener('scroll', onScroll);

    return () => { obs.disconnect(); scroller.removeEventListener('scroll', onScroll); };
  }, []);

  // Keyboard nav for case study — exact original logic
  useEffect(() => {
    if (openProject === null) return;
    const handler = (e) => {
      if (e.key === 'Escape')     setOpenProject(null);
      if (e.key === 'ArrowRight') setOpenProject((p) => (p + 1) % CASE_STUDIES.length);
      if (e.key === 'ArrowLeft')  setOpenProject((p) => (p - 1 + CASE_STUDIES.length) % CASE_STUDIES.length);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [openProject]);

  // Scroll-to-top button visibility
  useEffect(() => {
    const scroller = scrollRef.current;
    if (!scroller) return;
    const onScroll = () => setShowScrollTop(scroller.scrollTop > 400);
    scroller.addEventListener('scroll', onScroll);
    return () => scroller.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div style={I.root}>
      {/* Inject original CSS */}
      <style>{GLOBAL_CSS}</style>

      {/* Mobile header */}
      <MobileNav
        navItems={NAV_ITEMS}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
        scrollTo={scrollTo}
        setOpenProject={setOpenProject}
      />

      <div style={{ display: 'flex', flex: 1, overflow: 'hidden', position: 'relative' }} className="inner-layout">

        {/* Sidebar */}
        <Sidebar
          navItems={NAV_ITEMS}
          activeSection={activeSection}
          hoveredNav={hoveredNav}
          setHoveredNav={setHoveredNav}
          scrollTo={scrollTo}
          setOpenProject={setOpenProject}
        />

        {/* Right pane */}
        <div style={I.rightPane} className="right-pane">
          <main
            ref={scrollRef}
            className="main-scroll"
            style={{
              ...I.main,
              opacity:        openProject !== null ? 0 : 1,
              pointerEvents:  openProject !== null ? 'none' : 'auto',
              transition:     'opacity 0.3s ease',
            }}
          >
            <div id="hero" ref={setRef('hero')}>
              <HeroSection onViewWork={() => scrollTo('projects')} />
            </div>
            <div id="about" ref={setRef('about')}>
              <AboutSection />
            </div>
            <div id="projects" ref={setRef('projects')}>
              <ProjectsSection projects={CASE_STUDIES} onOpen={setOpenProject} />
            </div>
            <div id="timeline" ref={setRef('timeline')}>
              <CareerSection />
            </div>
            <div id="footer" ref={setRef('footer')}>
              <FooterSection />
            </div>
          </main>

          {/* Case study overlay */}
          {openProject !== null && (
            <CaseStudyDetail
              project={CASE_STUDIES[openProject]}
              index={openProject}
              total={CASE_STUDIES.length}
              onClose={() => setOpenProject(null)}
              onNext={() => setOpenProject((p) => (p + 1) % CASE_STUDIES.length)}
              onPrev={() => setOpenProject((p) => (p - 1 + CASE_STUDIES.length) % CASE_STUDIES.length)}
            />
          )}
        </div>
      </div>

      {/* Scroll-to-top button */}
      {showScrollTop && openProject === null && (
        <button
          onClick={() => scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' })}
          style={{
            position: 'fixed', bottom: '2rem', right: '2rem',
            width: '46px', height: '46px', borderRadius: '50%',
            background: '#1A1A1A', color: '#F7F5F2', border: 'none',
            cursor: 'pointer', display: 'flex', alignItems: 'center',
            justifyContent: 'center', fontSize: '1.1rem', zIndex: 90,
            boxShadow: '0 4px 20px rgba(0,0,0,0.18)',
            transition: 'transform 0.2s ease, opacity 0.2s ease',
            animation: 'fadeUp 0.3s ease both',
          }}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-3px)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
          aria-label="Scroll to top"
        >
          ↑
        </button>
      )}
    </div>
  );
}
